import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { getPaginationInfo } from '../utils/helpers';
import { useTranslation } from 'react-i18next';

const Pagination = ({
  total,
  page,
  limit,
  totalPages,
  onPageChange,
  showInfo = true,
  showPageNumbers = true,
  maxPageNumbers = 5,
}) => {
  const { t } = useTranslation();
  const { hasNextPage, hasPrevPage, startItem, endItem } = getPaginationInfo(total, page, limit);

  // Calculate which page numbers to show
  const getPageNumbers = () => {
    if (!showPageNumbers || totalPages <= 1) return [];
    
    const pages = [];
    const half = Math.floor(maxPageNumbers / 2);
    
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + maxPageNumbers - 1);
    
    // Adjust start if we're near the end
    if (end - start + 1 < maxPageNumbers) {
      start = Math.max(1, end - maxPageNumbers + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
      {showInfo && (
        <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
          <span className="hidden sm:inline">{t('pagination.showing')} </span>
          <span className="font-medium">{startItem}</span>
          <span className="hidden sm:inline"> {t('pagination.to')} </span>
          <span className="sm:hidden">-</span>
          <span className="font-medium">{endItem}</span>
          <span className="hidden sm:inline"> {t('pagination.of')} </span>
          <span className="sm:hidden"> / </span>
          <span className="font-medium">{total}</span>
          <span className="hidden sm:inline"> {t('pagination.results')}</span>
        </div>
      )}
      
      <div className="flex items-center space-x-1 sm:space-x-2 order-1 sm:order-2">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrevPage}
          className={`
            relative inline-flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md min-h-[44px] min-w-[44px]
            ${hasPrevPage 
              ? 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50' 
              : 'text-gray-300 bg-white border border-gray-300 cursor-not-allowed'
            }
          `}
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>

        {/* Page numbers */}
        {showPageNumbers && (
          <>
            {/* First page */}
            {pageNumbers[0] > 1 && (
              <>
                <button
                  onClick={() => onPageChange(1)}
                  className="relative inline-flex items-center justify-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 rounded-md min-h-[44px] min-w-[44px]"
                >
                  1
                </button>
                {pageNumbers[0] > 2 && (
                  <span className="text-gray-500">...</span>
                )}
              </>
            )}

            {/* Page numbers */}
            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`
                  relative inline-flex items-center justify-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md min-h-[44px] min-w-[44px]
                  ${pageNum === page
                    ? 'z-10 bg-blue-600 border-blue-600 text-white'
                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                {pageNum}
              </button>
            ))}

            {/* Last page */}
            {pageNumbers[pageNumbers.length - 1] < totalPages && (
              <>
                {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                  <span className="text-gray-500">...</span>
                )}
                <button
                  onClick={() => onPageChange(totalPages)}
                  className="relative inline-flex items-center justify-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 rounded-md min-h-[44px] min-w-[44px]"
                >
                  {totalPages}
                </button>
              </>
            )}
          </>
        )}

        {/* Next button */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage}
          className={`
            relative inline-flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md min-h-[44px] min-w-[44px]
            ${hasNextPage 
              ? 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50' 
              : 'text-gray-300 bg-white border border-gray-300 cursor-not-allowed'
            }
          `}
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;